import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaTrash, FaUpload, FaPaperclip, FaExternalLinkAlt, FaFileAlt, FaFilePdf, FaTimes } from "react-icons/fa";
import api from "../../../libs/axios";
import { uploadFilesWithPresignedPayload } from "../../../libs/presigned-upload";
import { useConfirm } from "../../../provider/confirm.provider";

// Shared multi-file panel for INC attachments (measurement book, BOQ and
// billing documents, visit files, …). Files go straight to S3 through presigned
// URLs and only the resulting descriptors are posted to the API, exactly like
// the survey module.
interface IncFilesPanelProps {
  // GET list / POST upload endpoints (same path in every INC module).
  filesUrl: string;
  // DELETE endpoint for one attachment.
  deleteUrl: (id: number) => string;
  // S3 folder the presigned URLs are generated under.
  folderName: string;
  canManage: boolean;
  title?: string;
  emptyLabel?: string;
  // When given, the uploader picks a document type for the batch and each file
  // shows its type. The first entry is the default.
  documentTypes?: Array<{ value: string; label: string }>;
  // Told the file count after every load, so a parent can badge it.
  onCountChange?: (count: number) => void;
}

const formatSize = (bytes?: number) => {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (f: any) => String(f?.mime_type || "").startsWith("image/");
const isPdf = (f: any) => String(f?.mime_type || "").includes("pdf") || /\.pdf$/i.test(f?.file_name || "");

const IncFilesPanel: React.FC<IncFilesPanelProps> = ({
  filesUrl,
  deleteUrl,
  folderName,
  canManage,
  title = "Documents",
  emptyLabel = "No documents uploaded.",
  documentTypes,
  onCountChange,
}) => {
  const { confirm } = useConfirm();
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<File[]>([]);
  const [description, setDescription] = useState("");
  const [documentType, setDocumentType] = useState(documentTypes?.[0]?.value || "");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the latest callback without making `load` depend on it — parents pass
  // inline arrows, which would otherwise re-fetch on every render.
  const countRef = useRef(onCountChange);
  countRef.current = onCountChange;

  const load = useCallback(async () => {
    try {
      const res = await api.get(filesUrl);
      const rows = res.data?.data || [];
      setFiles(rows);
      countRef.current?.(rows.length);
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, [filesUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const typeLabel = (value?: string) =>
    documentTypes?.find((t) => t.value === value)?.label || (value || "").replace(/_/g, " ");

  const clearSelection = () => {
    setSelected([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const upload = async () => {
    if (!selected.length) {
      toast.error("Choose at least one file");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const uploadedFiles = await uploadFilesWithPresignedPayload(
        selected.map((file) => ({ file, description: description.trim() || undefined })),
        { folderName, onProgress: (p) => setProgress(p.percentage) },
      );
      await api.post(filesUrl, {
        uploadedFiles,
        ...(documentTypes && documentType ? { document_type: documentType } : {}),
      });
      toast.success(`${selected.length} file${selected.length === 1 ? "" : "s"} uploaded`);
      clearSelection();
      setDescription("");
      load();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const remove = async (file: any) => {
    const ok = await confirm({
      title: "Delete document?",
      message: `"${file.file_name || `#${file.id}`}" will be removed permanently.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(deleteUrl(file.id));
      toast.success("Document deleted");
      load();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to delete document");
    }
  };

  return (
    <div>
      <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
        <FaPaperclip className="w-3 h-3" /> {title}
        {!loading && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{files.length}</span>}
      </div>

      {loading ? (
        <p className="text-xs text-gray-400 mb-2">Loading…</p>
      ) : files.length === 0 ? (
        <p className="text-xs text-gray-400 mb-2">{emptyLabel}</p>
      ) : (
        <ul className="text-sm divide-y divide-gray-100 border border-gray-200 rounded mb-3 bg-white">
          {files.map((f) => (
            <li key={f.id} className="px-3 py-2 flex justify-between items-center gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {/* Thumbnail for images, a type icon for everything else. */}
                {isImage(f) && f.url ? (
                  <a href={f.url} target="_blank" rel="noreferrer" className="flex-shrink-0">
                    <img src={f.url} alt="" className="w-10 h-10 object-cover rounded border border-gray-200" />
                  </a>
                ) : (
                  <div className="w-10 h-10 flex-shrink-0 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
                    {isPdf(f) ? <FaFilePdf className="w-4 h-4 text-red-500" /> : <FaFileAlt className="w-4 h-4 text-gray-400" />}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {f.url ? (
                      <a href={f.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline truncate">
                        {f.file_name || `#${f.id}`}
                      </a>
                    ) : (
                      <span className="truncate text-gray-700">{f.file_name || `#${f.id}`}</span>
                    )}
                    {f.url && <FaExternalLinkAlt className="w-2.5 h-2.5 text-gray-400 flex-shrink-0" />}
                    {documentTypes && f.document_type && (
                      <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-indigo-50 text-indigo-700 flex-shrink-0">
                        {typeLabel(f.document_type)}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {formatSize(f.file_size)}
                    {f.description ? ` · ${f.description}` : ""}
                    {f.uploader ? ` · ${f.uploader.first_name || ""} ${f.uploader.last_name || ""}`.trimEnd() : ""}
                    {f.uploaded_at ? ` · ${new Date(f.uploaded_at).toLocaleString()}` : ""}
                  </div>
                </div>
              </div>
              {canManage && (
                <button onClick={() => remove(f)} className="p-1.5 text-red-500 hover:bg-red-50 rounded flex-shrink-0" title="Delete">
                  <FaTrash className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="border border-dashed border-gray-300 rounded p-3 bg-gray-50/60">
          <div className="flex flex-wrap gap-2 items-center">
            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={(e) => setSelected(Array.from(e.target.files || []))}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50"
            >
              <FaPaperclip className="w-3 h-3" /> Choose files
            </button>
            {documentTypes && documentTypes.length > 0 && (
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
                title="Document type"
              >
                {documentTypes.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
            <input
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white flex-1 min-w-[160px]"
            />
            <button
              onClick={upload}
              disabled={uploading || selected.length === 0}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-blue-800 rounded hover:bg-blue-900 disabled:opacity-60"
            >
              <FaUpload className="w-3 h-3" />
              {uploading ? `Uploading ${progress}%` : `Upload${selected.length ? ` (${selected.length})` : ""}`}
            </button>
          </div>
          {selected.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {selected.map((file) => (
                <span key={`${file.name}-${file.size}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-white border border-gray-200 text-gray-700">
                  {file.name} <span className="text-gray-400">{formatSize(file.size)}</span>
                </span>
              ))}
              {!uploading && (
                <button type="button" onClick={clearSelection} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                  <FaTimes className="w-2.5 h-2.5" /> Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IncFilesPanel;
